import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  VStack,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  useDisclosure,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';

import {
  PropertyCarriersComparisonTable,
  MarketOptionsModal,
  RfqConfirmationModal,
  RfqErrorAlert,
  ValidationErrorsModal,
} from '../components/carriers';
import { useCarrierSorting } from '../hooks';
import AllPropertyCoveragesModal from '../components/AllPropertyCoveragesModal';
import {
  apiSubmitPropertyQuoteRfq,
  apiUpdateSelectedMarkets,
  apiSelectCarrierQuote,
  apiUpdateLocationCoverages,
} from 'api/quote-marketplace';
import type {
  PropertyCarriersData,
  CarriersLocation,
  LocationCoverages,
} from 'api/quote-marketplace';

interface PropertyCarriersStepProps {
  data: PropertyCarriersData;
  quoteId: number;
  onNavigateToTab: (tab: string) => void;
  onRefetch: () => void;
}

const PropertyCarriersStep = ({
  data,
  quoteId,
  onNavigateToTab,
  onRefetch,
}: PropertyCarriersStepProps) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editingLocation, setEditingLocation] =
    useState<CarriersLocation | null>(null);
  const {
    isOpen: isValidationModalOpen,
    onOpen: openValidationModal,
    onClose: closeValidationModal,
  } = useDisclosure();
  const {
    isOpen: isMarketOptionsOpen,
    onOpen: openMarketOptions,
    onClose: closeMarketOptions,
  } = useDisclosure();
  const {
    isOpen: isRfqConfirmationOpen,
    onOpen: openRfqConfirmation,
    onClose: closeRfqConfirmation,
  } = useDisclosure();
  const {
    isOpen: isCoveragesModalOpen,
    onOpen: openCoveragesModal,
    onClose: closeCoveragesModal,
  } = useDisclosure();

  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const submitRfqMutation = useMutation({
    mutationFn: () => apiSubmitPropertyQuoteRfq(quoteId),
    onSuccess: () => {
      toast({
        title: 'Quote request submitted',
        description: 'Fetching quotes from carriers...',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      onRefetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to submit quote request',
        description: error?.response?.data?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const updateSelectedMarketsMutation = useMutation({
    mutationFn: (selectedMarkets: string[]) =>
      apiUpdateSelectedMarkets(quoteId, selectedMarkets, 'property'),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['quote-marketplace', 'validation-schema', quoteId],
      });
      queryClient.invalidateQueries({
        queryKey: ['market-credentials', quoteId],
      });
      toast({
        title: 'Market selection updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
      closeMarketOptions();
      onRefetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update market selection',
        description: error?.response?.data?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const selectCarrierMutation = useMutation({
    mutationFn: (carrierQuoteId: number) =>
      apiSelectCarrierQuote(quoteId, carrierQuoteId, 'property'),
    onSuccess: () => {
      toast({
        title: 'Carrier selected',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
      queryClient.invalidateQueries({
        queryKey: ['property-quote-summary', quoteId],
      });
      queryClient.invalidateQueries({ queryKey: ['quote-group-summary'] });
      onRefetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to select carrier',
        description: error?.response?.data?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const updateCoveragesMutation = useMutation({
    mutationFn: ({
      locationId,
      coverages,
    }: {
      locationId: number;
      coverages: LocationCoverages;
    }) => apiUpdateLocationCoverages(quoteId, locationId, coverages),
    onSuccess: () => {
      toast({
        title: 'Coverages updated',
        description: 'Location coverages have been saved.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      closeCoveragesModal();
      setEditingLocation(null);
      onRefetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update coverages',
        description: error?.response?.data?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const handleGetQuotesClick = () => {
    if (!data.is_valid_for_rfq) {
      openValidationModal();
      return;
    }
    openRfqConfirmation();
  };

  const handleConfirmRfq = () => {
    submitRfqMutation.mutate();
    closeRfqConfirmation();
  };

  const handleMarketOptionsApply = (selectedMarkets: string[]) => {
    updateSelectedMarketsMutation.mutate(selectedMarkets);
  };

  const handleSelectCarrier = (carrierQuoteId: number) => {
    selectCarrierMutation.mutate(carrierQuoteId);
  };

  const handleEditCoverages = (locationId?: number) => {
    const targetLocation = locationId
      ? data.locations.find((l) => l.id === locationId)
      : data.locations[0];

    if (targetLocation) {
      setEditingLocation(targetLocation);
      openCoveragesModal();
    }
  };

  const handleSaveCoverages = (coverages: LocationCoverages) => {
    if (!editingLocation) return;

    updateCoveragesMutation.mutate({
      locationId: editingLocation.id,
      coverages,
    });
  };

  const handleCloseCoveragesModal = () => {
    closeCoveragesModal();
    setEditingLocation(null);
  };

  const hasError = data.rfq_status === 'error';
  const isCapturingRiskDetails = data.risk_details_sync?.status === 'pending';
  const hasRiskDetailsSyncError =
    !hasError && data.risk_details_sync?.status === 'error';
  const canSubmit =
    data.rfq_status === 'not_started' ||
    data.rfq_status === 'shell_created' ||
    data.rfq_status === 'error' ||
    data.rfq_status === 'complete';
  const hasSelectedCarrier = data.selected_carrier_quote_id !== null;

  const filteredCarriers =
    data.selected_markets.length > 0
      ? data.available_carriers.filter((c) =>
          data.selected_markets.includes(c.product),
        )
      : data.available_carriers;

  const { sortBy, setSortBy, sortedCarriers } = useCarrierSorting({
    carriers: filteredCarriers,
    quotes: data.carrier_quotes,
    locations: data.locations,
    insuranceHistory: data.insurance_history,
    selectedCarrierQuoteId: data.selected_carrier_quote_id,
  });

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {!data.is_valid_for_rfq && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Missing Required Information</AlertTitle>
              <AlertDescription>
                Some required fields are missing. Please complete all required
                information before requesting quotes.
                <Button
                  ml={2}
                  size="sm"
                  variant="link"
                  colorScheme="orange"
                  onClick={openValidationModal}
                >
                  View details
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {hasError && <RfqErrorAlert error={data.rfq_error} />}
        {hasRiskDetailsSyncError && (
          <RfqErrorAlert error={data.risk_details_sync.error} />
        )}

        {filteredCarriers.length === 0 ? (
          <Box>
            <Text color={labelColor} mb={4}>
              No carriers selected. Click "Market Options" to select carriers.
            </Text>
            <Button variant="outline" size="sm" onClick={openMarketOptions}>
              Market Options
            </Button>
          </Box>
        ) : data.locations.length === 0 ? (
          <Text color={labelColor}>
            No locations found. Please add locations before requesting quotes.
          </Text>
        ) : (
          <PropertyCarriersComparisonTable
            availableCarriers={sortedCarriers}
            carrierQuotes={data.carrier_quotes}
            locations={data.locations}
            insuranceHistory={data.insurance_history}
            selectedCarrierQuoteId={data.selected_carrier_quote_id}
            rfqStatus={data.rfq_status}
            onSelectCarrier={handleSelectCarrier}
            onEditCoverages={handleEditCoverages}
            onOpenMarketOptions={openMarketOptions}
            onGetQuotes={handleGetQuotesClick}
            isSelectingCarrier={selectCarrierMutation.isPending}
            isGettingQuotes={submitRfqMutation.isPending}
            canGetQuotes={
              canSubmit && data.is_valid_for_rfq && !isCapturingRiskDetails
            }
            isCapturingRiskDetails={isCapturingRiskDetails}
            riskDetailsSync={data.risk_details_sync}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />
        )}
      </VStack>

      <ValidationErrorsModal
        isOpen={isValidationModalOpen}
        onClose={closeValidationModal}
        errors={data.validation_errors}
        onNavigateToTab={onNavigateToTab}
      />

      <MarketOptionsModal
        isOpen={isMarketOptionsOpen}
        onClose={closeMarketOptions}
        availableCarriers={data.available_carriers}
        selectedMarkets={data.selected_markets}
        onApply={handleMarketOptionsApply}
        isLoading={updateSelectedMarketsMutation.isPending}
      />

      <RfqConfirmationModal
        isOpen={isRfqConfirmationOpen}
        onClose={closeRfqConfirmation}
        onConfirm={handleConfirmRfq}
        selectedCarriers={filteredCarriers}
        isLoading={submitRfqMutation.isPending}
        hasSelectedCarrier={hasSelectedCarrier}
      />

      {editingLocation && (
        <AllPropertyCoveragesModal
          isOpen={isCoveragesModalOpen}
          onClose={handleCloseCoveragesModal}
          onSave={handleSaveCoverages}
          initialCoverages={
            editingLocation.coverages as unknown as LocationCoverages
          }
          location={editingLocation}
          isUpdating={updateCoveragesMutation.isPending}
          quoteId={quoteId}
          province={data.province}
        />
      )}
    </Box>
  );
};

export default PropertyCarriersStep;
